/**
 * `TabLoader` — the loading / error / empty envelope every lazy machine-page
 * tab shares.
 *
 * Each tab fetches its own domain (`work-orders`, `costs`, `rms`, `doc-index`,
 * or one machine's Maximo record) rather than the page loading all five up
 * front, which would be ~1.9 MB for a page most readers open on one tab. The
 * envelope keeps that decision from being restated five times.
 *
 * Skeletons, not spinners: the tab's height is roughly known, so the layout
 * does not jump when the payload lands.
 *
 * UI layer.
 */

import type { ReactNode } from 'react';
import { InlineError, SkeletonRows } from '@/design-system';

export interface TabLoaderProps {
  loading: boolean;
  error: Error | null;
  /** What failed, for the error line: `'Work orders'`, `'The Maximo record'`. */
  what: string;
  /** Skeleton row count while loading. */
  rows?: number;
  children: ReactNode;
}

export function TabLoader({ loading, error, what, rows = 6, children }: TabLoaderProps) {
  if (error) return <InlineError>{`${what} did not load: ${error.message}`}</InlineError>;
  if (loading) return <SkeletonRows count={rows} />;
  return <>{children}</>;
}
